<template>
  <div class="container card-container">
    <div class="card shadow-sm p-3">
      <!-- Imagen perfil con VueCropper -->
      <div class="card-head text-center position-relative d-flex justify-content-center align-items-center flex-column">

        <vue-cropper
            v-if="showCropper"
            ref="cropper"
            :guides="false"
            :src="image"
            :autoCropArea="1"
            :aspectRatio="1"
            :viewMode="1"
            :dragMode="dragMode"
            class="crapper-container mx-auto"
            @ready="applyCircularCrop"
        ></vue-cropper>

        <img
            v-if="!showCropper"
            :src="croppedImage || editedUser.img_path || tmp_img_path"
            class="rounded-circle img-fluid image-hover profile-img"
            alt="Profile Image"
            @click="openFileInput"
        />

        <label v-if="!showCropper" for="fileInput" class="upload-icon">
          <i class="fas fa-plus-circle fa-2xl border"></i>
        </label>
        <input
            v-if="!showCropper"
            type="file"
            ref="fileInput"
            id="fileInput"
            class="d-none"
            @change="handleImageUpload"
        />

        <!-- Botones de Acción -->
        <div v-if="showCropper" class="mt-2">
          <button class="btn btn-warning btn-sm" @click="saveCroppedImage">
            {{$t('competitions.btn_save')}}
          </button>
          <button class="btn btn-danger btn-sm ms-2" @click="cancelCropping">
            {{$t('competitions.btn_close')}}
          </button>
        </div>
      </div>
      <div class="card-body text-center mt-5">
        <h5 class="card-title mb-1">{{ editedUser.name }} {{ editedUser.surname }}</h5>
        <p class="text-muted small mb-2">{{ editedUser.email }}</p>

        <!-- Verificación -->
        <div class="mb-3 d-flex align-items-center justify-content-center flex-wrap">
          <!-- Badge de estado -->
          <span :class="['badge', 'bg-' + verificationStatus.type, verificationStatus.type === 'warning' ? 'text-dark' : '']">
            {{ verificationStatus.label }}
          </span>
          <!-- Botón "Verificar ahora" -->
          <button
              v-if="!editedUser.email_verified_at && !editedUser.email_verification_sent_at"
              class="btn btn-sm btn-outline-primary ms-2 d-flex align-items-center"
              @click="resendVerificationEmail"
          >
            <i class="fas fa-check"></i>
            <span class="d-none d-sm-inline ms-1">{{ $t('profile.verified_now') }}</span>
          </button>
        </div>



        <!-- Roles -->
        <div class="section-box">
          <div class="d-flex justify-content-between align-items-center">
            <div class="section-title">{{ $t('profile.role_title') }}</div>
            <button
                :disabled="!canAddRole"
                class="btn btn-sm btn-outline-primary d-flex align-items-center"
                @click="openAddRoleModal"
            >
              <i class="fas fa-circle-plus"></i>
              <span class="d-none d-sm-inline ms-1">{{ $t('profile.new_role') }}</span>
            </button>
          </div>
          <div class="roles-list mt-2" v-if="editedUser && Array.isArray(editedUser.roles) && editedUser.roles.length">
            <span
                v-for="role in editedUser.roles"
                :key="role.id"
                class="bg-secondary badge me-1 position-relative role-pill"
            >
              {{ role.name }}
              <button
                  v-if="canRemoveRoles"
                  type="button"
                  class="btn btn-link btn-sm text-light p-0 ms-1 align-baseline remove-role-btn"
                  :disabled="editedUser.roles.length <= 1"
                  @click.stop="confirmRemoveRole(role)"
                  :aria-label="$t('profile.delete_role') || 'Remove role'"
              >
                <i class="fas fa-times"></i>
              </button>
            </span>
          </div>
        </div>

        <div v-if="showRolesModal" class="roles-modal-backdrop" @click.self="closeRolesModal">
          <div class="roles-modal card shadow-sm p-3">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0">{{ $t('profile.new_role') }}</h6>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeRolesModal">×</button>
            </div>
            <div class="mb-3">
              <label class="form-label">{{ $t('profile.select_role') || 'Select role' }}</label>
              <select class="form-select" v-model="selectedRoleId">
                <option v-for="role in availableRoles" :key="role.id" :value="role.id">
                  {{ role.name }}
                </option>
              </select>
            </div>
            <div class="d-flex justify-content-end gap-2">
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeRolesModal">
                {{ $t('profile.cancel_btn') }}
              </button>
              <button type="button" class="btn btn-primary btn-sm" :disabled="!selectedRoleId" @click="confirmAddRole">
                {{ $t('profile.save_btn') }}
              </button>
            </div>
          </div>
        </div>


        <!-- Datos personales -->
        <div class="section-box text-start">
          <div class="section-title">{{ $t('profile.title_data') }}</div>
          <div class="row">
            <div class="col-md-4 mb-2">
              <label class="form-label">{{ $t('profile.username') }}</label>
              <input type="text" class="form-control" v-model="editedUser.username">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">{{ $t('profile.name') }}</label>
              <input type="text" class="form-control" v-model="editedUser.name">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">{{ $t('profile.surname') }}</label>
              <input type="text" class="form-control" v-model="editedUser.surname">
            </div>
            <div class="col-md-6 mb-2">
              <label class="form-label">{{ $t('profile.phone') }}</label>
              <div class="d-flex phone-row">
                <div class="input-group" style="max-width: 140px;">
                  <span class="input-group-text">+</span>
                  <input
                      type="text"
                      class="form-control phone-code-input"
                      v-model="phoneCode"
                      @input="updatePhone"
                      placeholder="34"
                  />
                </div>
                <input
                    type="tel"
                    class="form-control phone-number-input"
                    v-model="rawPhone"
                    @input="updatePhone"
                    placeholder="612345678"
                />
              </div>
              <div class="form-text phone-preview" v-if="normalizedPhonePreview">
                {{ $t('profile.phone') }}: {{ normalizedPhonePreview }}
              </div>
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label">{{ $t('profile.language') }}</label>
              <select class="form-select" v-model="editedUser.language" @change="updateLocale">
                <option
                    v-for="lang in supportedLanguages"
                    :key="lang.code"
                    :value="lang.code"
                >
                  {{ lang.label }}
                </option>
              </select>
            </div>
            <div class="col-md-3 mb-2">
              <SelectType
                  v-model="editedUser.country_id"
                  :options="countries"
                  :label="$t('profile.country')"
                  :placeholder="$t('profile.select_country')"
                  id="country_id"
                  :required="true"
                  customClass="border-primary"
                  errorMessage="Este campo es obligatorio"
              />
            </div>
            <div class="col-md-4 mb-2">
              <SelectType
                  v-model="editedUser.brand_sponsor_id"
                  :options="brandSponsors"
                  :label="$t('profile.sponsor')"
                  :placeholder="$t('profile.select_sponsor')"
                  id="brand_sponsor_id"
                  :required="false"
                  customClass="border-primary"
              />
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">{{ $t('profile.group_name') }}</label>
              <input type="text" class="form-control" v-model="editedUser.group_name" :placeholder="$t('profile.group_name')">
            </div>
            <div class="col-md-3 mb-2 d-flex align-items-center">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="isa_member" v-model="editedUser.isa_member">
                <label class="form-check-label" for="isa_member">{{ $t('profile.isa_member') }}</label>
              </div>
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label">{{ $t('profile.birthday') }}</label>
              <input type="date" class="form-control" v-model="editedUser.birthday">
            </div>
            <div class="col-md-3 mb-2">
              <label class="form-label">{{ $t('profile.gender') }}</label>
              <select class="form-select" v-model="editedUser.gender">
                <option value="">{{ $t('profile.select_gender') }}</option>
                <option value="male">{{ $t('profile.gender_male') }}</option>
                <option value="female">{{ $t('profile.gender_female') }}</option>
              </select>
            </div>
            <div class="col-md-2 mb-2">
              <label class="form-label text-nowrap">{{ $t('profile.since') }}</label>
              <input type="number" class="form-control" v-model="editedUser.slackline_since" min="1990" max="2025">
            </div>
          </div>
        </div>

        <!-- Redes Sociales -->
        <div class="section-box text-start">
          <div class="section-title">{{ $t('profile.social_networks') }}</div>
          <div class="row">
            <div class="col-md-4 mb-2">
              <label class="form-label">{{ $t('profile.website') }}</label>
              <input type="url" class="form-control" v-model="editedUser.social_links.website" placeholder="example.com">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">{{ $t('profile.instagram') }}</label>
              <input type="url" class="form-control" v-model="editedUser.social_links.instagram" placeholder="@YourUser">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">{{ $t('profile.youtube') }}</label>
              <input type="url" class="form-control" v-model="editedUser.social_links.youtube" placeholder="@YourUser">
            </div>
          </div>
        </div>

        <div class="section-box text-start">
          <div class="section-title">{{ identityTexts.title }}</div>
          <p class="text-muted small mb-3">{{ identityTexts.description }}</p>

          <div
            v-for="role in configurableDiscoveryRoles"
            :key="role.id"
            class="identity-role-card"
          >
            <div class="identity-role-title">{{ role.name }}</div>

            <div class="form-check form-switch mb-3">
              <input
                :id="`profile-visible-toggle-${role.id}`"
                v-model="role.pivot.is_profile_visible"
                class="form-check-input"
                type="checkbox"
                :disabled="editedUser.is_identity_sanitized"
              >
              <label class="form-check-label" :for="`profile-visible-toggle-${role.id}`">
                {{ getRoleVisibleLabel(role) }}
              </label>
              <div class="form-text">{{ getRoleVisibleHelp(role) }}</div>
            </div>

            <div class="form-check form-switch mb-0">
              <input
                :id="`competition-selectable-toggle-${role.id}`"
                v-model="role.pivot.is_selectable_for_competitions"
                class="form-check-input"
                type="checkbox"
                :disabled="editedUser.is_identity_sanitized"
              >
              <label class="form-check-label" :for="`competition-selectable-toggle-${role.id}`">
                {{ getRoleSelectableLabel(role) }}
              </label>
              <div class="form-text">{{ getRoleSelectableHelp(role) }}</div>
            </div>
          </div>

          <div v-if="editedUser.is_identity_sanitized" class="alert alert-secondary small mb-0">
            {{ identityTexts.sanitizedState }}
          </div>
          <div v-else-if="hasPendingSanitizationRequest" class="alert alert-warning small mb-0">
            {{ identityTexts.sanitizePending }}
          </div>
          <div v-else-if="identityRequestStatusMessage" :class="['alert', `alert-${identityRequestStatusType === 'error' ? 'danger' : 'info'}`, 'small', 'mb-0']">
            {{ identityRequestStatusMessage }}
          </div>
        </div>

      </div>
      <div class="car-footer d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-end mt-4 gap-3">
        <!-- Guardar y Cancelar -->
        <div class="button-group d-flex flex-column flex-sm-row gap-2 order-1 order-sm-2">
          <button class="btn btn-primary btn-sm w-100 w-sm-auto" @click="saveProfile">
            {{ $t('profile.save_btn') }}
          </button>
          <button class="btn btn-outline-secondary btn-sm w-100 w-sm-auto" @click="$emit('cancel-edit')">
            {{ $t('profile.cancel_btn') }}
          </button>
        </div>

        <!-- Eliminar cuenta -->
        <div class="alert-box order-2 order-sm-1">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <i class="fas fa-exclamation-triangle me-2 text-danger"></i>
            <button
              class="btn btn-outline-danger btn-sm"
              :disabled="sanitizingIdentity || editedUser.is_identity_sanitized || !editedUser.email_verified_at || hasPendingSanitizationRequest"
              @click="sanitizeIdentity"
            >
              {{ identityTexts.sanitizeButton }}
            </button>
            <span class="small text-muted">{{ identityTexts.sanitizeHelp }}</span>
            <span v-if="!editedUser.email_verified_at && !editedUser.is_identity_sanitized" class="small text-warning">
              {{ identityTexts.sanitizeRequiresVerifiedEmail }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <ConfirmationModal
      ref="confirmationModal"
      :message="modalMessage"
      :showCancel="showCancel"
      :showRuleSelector="false"
      :ruleOptions="[]"
      :showNameInput="false"
      :initialRule="''"
      :initialName="''"
      @confirmed="handleConfirmed"
      @canceled="cancelModal"
  />
  <loading-modal ref="loadingModal"></loading-modal>
</template>

<script>

import VueCropper from "vue-cropperjs";
import 'cropperjs/dist/cropper.css';
import ConfirmationModal from "@/components/competitions/ConfirmationModal.vue";
import LoadingModal from "@/services/LoadingModal.vue";
import {secureStorage} from "@/services/secureStorage";
import { supportedLanguages } from '@/constants/languages';
import SelectType from "@/components/competitions/SelectType.vue";
import {mapActions, mapState} from "vuex";
import echoService from "@/services/echoService";
export default {
  name: 'ProfileEdit',
  emits: ['cancel-edit', 'update-user'],
  components: {
    ConfirmationModal,
    SelectType,
    LoadingModal,
    VueCropper,
  },
  data() {
    return {
      editedUser: {}, // se llena en mounted o en watch
      image: null,
      showCropper: false,
      tmp_img_path: require('@/assets/user.png'),
      croppedImage: null,
      dragMode: 'move',
      supportedLanguages,
      countries: [],
      brandSponsors: [],
      phoneCode: '',
      rawPhone: '',
      verificationStatusMessage: null, // mensaje opcional temporal
      verificationStatusType: null,    // puede ser 'info', 'success', 'error'
      identityRequestStatusMessage: null,
      identityRequestStatusType: null,
      sanitizingIdentity: false,
      sanitizationNow: Date.now(),
      sanitizationClock: null,
      rolesOptions: [],
      showRolesModal: false,
      selectedRoleId: null,
      pendingConfirmResolver: null,
      showCancel: false,
      modalMessage: ''
    };
  },
  watch: {
    user: {
      handler() {
        this.syncUser();
      },
      deep: true,
      immediate: true,
    }
  },
  computed: {
    ...mapState('user', ['user','token']),
    canAddRole() {
      const roles = Array.isArray(this.editedUser?.roles) ? this.editedUser.roles : [];
      return roles.length < 3;
    },
    canRemoveRoles() {
      const roles = Array.isArray(this.editedUser?.roles) ? this.editedUser.roles : [];
      return roles.length > 0;
    },
    availableRoles() {
      const currentIds = (this.editedUser?.roles || []).map(r => r.id);
      return this.rolesOptions.filter(role => !currentIds.includes(role.id));
    },
    configurableDiscoveryRoles() {
      return (this.editedUser?.roles || []).filter(role => this.isDiscoveryRoleConfigurable(role));
    },
    normalizedPhonePreview() {
      const code = this.normalizeCode(this.phoneCode);
      const phone = this.normalizePhone(this.rawPhone);
      if (!code && !phone) return '';
      return code ? `00${code}${phone}` : phone;
    },
    verificationStatus() {
      if (this.verificationStatusMessage) {
        return {
          label: this.verificationStatusMessage,
          type: this.verificationStatusType || 'info'
        };
      }

      if (this.editedUser.email_verified_at) {
        return {
          label: this.$t('profile.mail_verified'),
          type: 'success'
        };
      }

      if (this.editedUser.email_verification_sent_at) {
        return {
          label: this.$t('profile.mail_pending'),
          type: 'warning'
        };
      }

      return {
        label: this.$t('profile.mail_not_verified'),
        type: 'danger'
      };
    },
    identityTexts() {
      const translate = (key, fallback) => (typeof this.$te === 'function' && this.$te(key) ? this.$t(key) : fallback);

      return {
        title: translate('profile.identity_controls_title', 'Identity and availability'),
        description: translate('profile.identity_controls_description', 'Control your visibility and invitation availability separately for each role in your account.'),
        visibleLabel: translate('profile.identity_visible_label', 'Show profile'),
        visibleHelp: translate('profile.identity_visible_help', 'Disable this to hide your profile from the {role} listing.'),
        selectableLabel: translate('profile.identity_selectable_label', 'Allow invitations'),
        selectableHelp: translate('profile.identity_selectable_help', 'Disable this if you do not want to be invited in future competitions as {role}.'),
        sanitizeButton: translate('profile.identity_sanitize_button', 'Request account deletion'),
        sanitizeHelp: translate('profile.identity_sanitize_help', 'We will send you a confirmation email to finish the account sanitization securely.'),
        sanitizeConfirm: translate('profile.identity_sanitize_confirm', 'We will send a confirmation email to your verified address. The account will only be sanitized after you open that email and complete the confirmation. Continue?'),
        sanitizedState: translate('profile.identity_sanitized_state', 'This account has already been sanitized. It stays linked to historical statistics, but remains hidden from listings and future selections.'),
        saveError: translate('profile.identity_save_error', 'Could not save identity preferences.'),
        sanitizeError: translate('profile.identity_sanitize_error', 'Could not sanitize the account.'),
        sanitizeRequestSent: translate('profile.identity_sanitize_request_sent', 'We sent you a confirmation email. Open your inbox and finish the operation from that link.'),
        sanitizePending: translate('profile.identity_sanitize_pending', 'A sanitization request is already pending. Check your email to finish the operation.'),
        sanitizeRequiresVerifiedEmail: translate('profile.identity_sanitize_requires_verified_email', 'Verify your email before requesting account sanitization.'),
      };
    },
    hasPendingSanitizationRequest() {
      if (this.editedUser?.is_identity_sanitized) {
        return false;
      }

      const expiresAt = this.editedUser?.sanitization_request_expires_at;
      if (!expiresAt) {
        return Boolean(this.editedUser?.sanitization_request_pending);
      }

      const expiresTime = new Date(expiresAt).getTime();
      return Number.isFinite(expiresTime) && expiresTime > this.sanitizationNow;
    }
  },
  mounted() {
    this.loadCountries();
    this.loadSponsors();
    this.hydratePhoneFields(this.editedUser.phone);
    if (this.user) {
      this.listenToUserUpdates(this.user.id);
    }
    this.syncUser();
    this.sanitizationClock = window.setInterval(() => {
      this.sanitizationNow = Date.now();
    }, 60000);
  },
  beforeUnmount() {
    if (this.sanitizationClock) {
      window.clearInterval(this.sanitizationClock);
      this.sanitizationClock = null;
    }
  },
  methods: {
    ...mapActions('user', ['setLogout','setUser']),
    listenToUserUpdates(userId) {
      echoService.channel(`users.${userId}`)
          .listen('.UserUpdated', (e) => {
            const updatedUser = {
              ...this.user,
              ...e.user // solo actualiza los campos nuevos
            };

            this.setUser(updatedUser);
          })
          .error(error => {
            console.error('❌ Error en WebSocket:', error);
          });
    },
    syncUser() {
      const copy = JSON.parse(JSON.stringify(this.user));
      if (!copy) {
        this.editedUser = {};
        return;
      }

      if (typeof copy.social_links === 'string') {
        try {
          copy.social_links = JSON.parse(copy.social_links);
        } catch {
          copy.social_links = {};
        }
      }

      copy.social_links = {
        website: copy.social_links?.website || '',
        instagram: copy.social_links?.instagram || '',
        youtube: copy.social_links?.youtube || ''
      };

      // Normalizar campos nuevos
      copy.group_name = copy.group_name || '';
      copy.isa_member = !!copy.isa_member;
      copy.birthday = copy.birthday ? String(copy.birthday).slice(0, 10) : '';
      copy.gender = copy.gender || '';
      copy.is_identity_sanitized = !!copy.is_identity_sanitized;
      copy.sanitization_requested_at = copy.sanitization_requested_at || null;
      copy.sanitization_request_pending = !!copy.sanitization_request_pending;
      copy.sanitization_request_expires_at = copy.sanitization_request_expires_at || null;
      copy.roles = this.normalizeRoles(copy.roles || []);

      this.editedUser = copy;
    },
    normalizeRoles(roles) {
      return (Array.isArray(roles) ? roles : []).map(role => ({
        ...role,
        pivot: {
          is_profile_visible: this.normalizeDiscoveryFlag(role?.pivot?.is_profile_visible),
          is_selectable_for_competitions: this.normalizeDiscoveryFlag(role?.pivot?.is_selectable_for_competitions),
        }
      }));
    },
    isDiscoveryRoleConfigurable(role) {
      const roleName = String(role?.name || '').trim().toLowerCase();
      return roleName !== 'admin' && roleName !== 'supervisor';
    },
    normalizeDiscoveryFlag(value) {
      if (value === undefined || value === null) {
        return true;
      }

      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'false' || normalized === '0' || normalized === '') {
          return false;
        }
        if (normalized === 'true' || normalized === '1') {
          return true;
        }
      }

      return Boolean(value);
    },
    interpolateRoleText(template, roleName) {
      return String(template || '').replace('{role}', roleName || 'role');
    },
    getRoleVisibleLabel() {
      return this.identityTexts.visibleLabel;
    },
    getRoleVisibleHelp(role) {
      return this.interpolateRoleText(this.identityTexts.visibleHelp, role?.name || 'role');
    },
    getRoleSelectableLabel() {
      return this.identityTexts.selectableLabel;
    },
    getRoleSelectableHelp(role) {
      return this.interpolateRoleText(this.identityTexts.selectableHelp, role?.name || 'role');
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.image = e.target.result;
          this.showCropper = true;
        };
        reader.readAsDataURL(file);
      }
    },
    applyCircularCrop() {
      const cropperElement = this.$refs.cropper.$el;
      const cropperViewBox = cropperElement.querySelector('.cropper-view-box');
      const cropperFace = cropperElement.querySelector('.cropper-face');
      if (cropperViewBox && cropperFace) {
        cropperViewBox.style.borderRadius = '50%';
        cropperFace.style.borderRadius = '50%';
      }
    },
    openFileInput() {
      this.$refs.fileInput.click();
    },
    saveCroppedImage() {
      if (!this.$refs.cropper) return;
      const canvas = this.$refs.cropper.getCroppedCanvas({
        width: 400,
        height: 400,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      });
      if (!canvas) return;
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        this.croppedImage = URL.createObjectURL(blob);
        this.editedUser.img_path = this.croppedImage;
        this.showCropper = false;
        await this.uploadProfileImage(blob);
      }, 'image/jpeg', 0.7);
    },
    cancelCropping() {
      this.showCropper = false;
    },
    async uploadProfileImage(blob) {
      const formData = new FormData();
      formData.append('image', new File([blob], 'profile.jpg', { type: blob.type }));
      formData.append('user_id', this.user.id);
      try {
        this.showLoading();
        const response = await this.$axios.post('/upload-profile-image', formData, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'multipart/form-data'
          }
        });
        if (response.status === 200) {
          const basePath = response.data.path;
          const newImgPath = basePath + '?t=' + new Date().getTime(); // 🔁 fuerza recarga

          const updatedUser = {
            ...this.$store.state.user,
            img_path: newImgPath
          };

          // 1. Actualiza Vuex
          this.$store.commit('setUserMut', { user: updatedUser });

          // 2. Actualiza secureStorage (para persistencia tras reload)
          secureStorage.setItem('profile', JSON.stringify(updatedUser));

          // 3. Actualiza esta vista también
          this.editedUser.img_path = newImgPath;
          this.hideLoading();
        } else {
          throw new Error(response.data.error || 'Error desconocido');
        }
      } catch (error) {
        console.error('❌ Error en la subida de la imagen:', error);
        this.hideLoading();
      }
    },
    async resendVerificationEmail() {
      try {
        this.showLoading();
        const response = await this.$axios.post('/resend-verification-email', null, {
          headers: {
            Authorization: `Bearer ${this.token}`,
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (response.status === 200) {
          this.setUser(response.data.user);

          this.verificationStatusMessage = this.$t('profile.verification_email_sent');
          this.verificationStatusType = 'info';
        }else {
          this.verificationStatusMessage = this.$t('profile.verification_email_failed');
          this.verificationStatusType = 'error';
        }
      } catch (error) {
        console.error('❌ Error enviando verificación:', error);
        this.verificationStatusMessage = this.$t('profile.verification_email_failed');
        this.verificationStatusType = 'error';
      } finally {
        this.hideLoading();
      }
    },
    showLoading() {
      this.$nextTick(() => {
        this.$refs.loadingModal?.showLoading();
      });
    },
    hideLoading() {
      this.$nextTick(() => {
        this.$refs.loadingModal?.hideLoading();
      });
    },
    saveProfile() {
      const countryOption = this.countries.find(c => c.id === this.editedUser.country_id);
      const countryCode = countryOption?.iso2 || (typeof this.editedUser.country === 'string' ? this.editedUser.country : null);

      const payload = {
        name: this.editedUser.name || '',
        surname: this.editedUser.surname || '',
        username: this.editedUser.username || '',
        email: this.editedUser.email || '',
        phone: this.editedUser.phone || '',
        language: this.editedUser.language || '',
        gender: this.editedUser.gender || '',
        birthday: this.editedUser.birthday || '',
        slackline_since: this.editedUser.slackline_since || '',
        bio: this.editedUser.bio || '',
        social_links: this.editedUser.social_links || {},
        experience_level: this.editedUser.experience_level || '',
        group_name: this.editedUser.group_name || '',
        isa_member: !!this.editedUser.isa_member,
        brand_sponsor_id: this.editedUser.brand_sponsor_id || null,
        country_id: countryOption?.id || null,
        country: countryCode || '',
        img_path: this.editedUser.img_path || '',
        role_settings: (this.editedUser.roles || []).map(role => ({
          role_id: role.id,
          is_profile_visible: !!role?.pivot?.is_profile_visible,
          is_selectable_for_competitions: !!role?.pivot?.is_selectable_for_competitions,
        })),
      };

      this.$emit('update-user', payload);
      this.$emit('cancel-edit');
    },
    async sanitizeIdentity() {
      const isConfirmed = await this.confirmWithModal(this.identityTexts.sanitizeConfirm);
      if (!isConfirmed) {
        return;
      }

      try {
        this.sanitizingIdentity = true;
        this.showLoading();
        const response = await this.$axios.post('/me/request-sanitization', {}, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (response?.data?.user) {
          const normalizedUser = {
            ...response.data.user,
            roles: this.normalizeRoles(response.data.user.roles || []),
          };

          this.editedUser = {
            ...this.editedUser,
            ...normalizedUser,
          };
          this.setUser(normalizedUser);
        }

        this.identityRequestStatusMessage = this.identityTexts.sanitizeRequestSent;
        this.identityRequestStatusType = 'info';
        this.openInfoModal(this.identityTexts.sanitizeRequestSent);
      } catch (error) {
        console.error('Error sanitizing identity:', error);
        const message = error?.response?.data?.errors?.message?.[0]
          || error?.response?.data?.message
          || this.identityTexts.sanitizeError;
        this.identityRequestStatusMessage = message;
        this.identityRequestStatusType = 'error';
        this.openInfoModal(message);
      } finally {
        this.sanitizingIdentity = false;
        this.hideLoading();
      }
    },
    updateLocale() {
      this.$i18n.locale = this.editedUser.language;
      localStorage.setItem('lang', this.editedUser.language);
    },
    async loadCountries() {
      try {
        this.showLoading();
        const response = await this.$axios.get("/countries",{
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
            .catch(error => {
              console.error('Error al obtener la lista de países:', error);
            });
        const countriesData = response.data.countries.map(country => {
          let translatedName = country.name;
          let langCode = this.$i18n.locale;
          if (langCode !== 'en' && country.translations) {
            try {
              const translationsObject = JSON.parse(country.translations);
              if (langCode === 'jp'){langCode = 'ja'}
              translatedName = translationsObject[langCode] || country.name;
            } catch (error) {
              console.error('Error al parsear las traducciones:', error);
            }
          }

          return {
            id: country.id,
            name: translatedName,
            currency_symbol: country.currency_symbol,
            iso2: country.iso2
          };
        });

        this.countries = countriesData.sort((a, b) => {
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();
          return nameA.localeCompare(nameB);
        });
      } catch (error) {
        console.error('Error loading countries:', error);
      } finally {
        this.hideLoading();
      }
    },
    async loadSponsors() {
      try {
        this.showLoading();
        const response = await this.$axios.get('/sponsors', {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        }).catch(error => {
          console.error('Error al obtener la lista de sponsors:', error);
        });

        const sponsorsData = response?.data?.sponsors?.map(sponsor => ({
          id: sponsor.id,
          name: sponsor.name,
        })) || [];

        this.brandSponsors = sponsorsData.sort((a, b) => {
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();
          return nameA.localeCompare(nameB);
        });
      } catch (error) {
        console.error('Error loading sponsors:', error);
      } finally {
        this.hideLoading();
      }
    },
    updatePhone() {
      const code = this.normalizeCode(this.phoneCode);
      const phone = this.normalizePhone(this.rawPhone);
      if (code && phone) {
        this.editedUser.phone = `00${code}${phone}`;
      } else if (phone) {
        this.editedUser.phone = phone;
      } else {
        this.editedUser.phone = '';
      }
    },
    normalizeCode(value) {
      const digits = String(value || '').replace(/\D/g, '');
      return digits.replace(/^0+/, '');
    },
    normalizePhone(value) {
      const digits = String(value || '').replace(/\D/g, '');
      return digits;
    },
    hydratePhoneFields(fullPhone) {
      const digits = String(fullPhone || '').replace(/\D/g, '');
      if (!digits) {
        this.phoneCode = '';
        this.rawPhone = '';
        return;
      }
      const trimmed = digits.replace(/^0+/, '');
      const assumedPhoneLen = 9;
      if (trimmed.length > assumedPhoneLen) {
        const codeLen = Math.min(trimmed.length - assumedPhoneLen, 4);
        this.phoneCode = trimmed.slice(0, codeLen);
        this.rawPhone = trimmed.slice(-assumedPhoneLen);
      } else {
        this.phoneCode = '';
        this.rawPhone = trimmed;
      }
    },
    async openAddRoleModal() {
      await this.fetchRolesOptions();
      this.selectedRoleId = this.availableRoles[0]?.id || null;
      this.showRolesModal = true;
    },
    closeRolesModal() {
      this.showRolesModal = false;
      this.selectedRoleId = null;
    },
    async fetchRolesOptions() {
      try {
        const response = await this.$axios.get('/roles');
        const blocked = ['Admin', 'Supervisor'];
        this.rolesOptions = (response.data.roles || []).filter(role => !blocked.includes(role.name));
      } catch (error) {
        console.error('Error fetching roles:', error);
        this.rolesOptions = [];
      }
    },
    async confirmAddRole() {
      const role = this.rolesOptions.find(r => r.id === this.selectedRoleId);
      if (!role) return;
      const isConfirmed = await this.confirmWithModal(this.$t('profile.confirm_add_role') || `¿Añadir el rol ${role.name}?`);
      if (!isConfirmed) {
        return;
      }
      try {
        this.showLoading();
        const response = await this.$axios.post('/me/roles', { role_ids: [role.id] }, {
          headers: { Authorization: `Bearer ${this.token}` }
        });

        if (response?.data?.user) {
          this.applyUserUpdate(response.data.user);
        }

        this.closeRolesModal();
      } catch (error) {
        console.error('Error adding role:', error);
      } finally {
        this.hideLoading();
      }
    },
    async confirmRemoveRole(role) {
      const currentRoles = this.editedUser?.roles || [];
      if (currentRoles.length <= 1) {
        this.openInfoModal(this.$t('profile.at_least_one_role') || 'Debes conservar al menos un rol.');
        return;
      }
      const isConfirmed = await this.confirmWithModal(this.$t('profile.confirm_remove_role') || `¿Eliminar el rol ${role.name}?`);
      if (!isConfirmed) {
        return;
      }
      try {
        this.showLoading();
        const response = await this.$axios.delete(`/me/roles/${role.id}`, {
          headers: { Authorization: `Bearer ${this.token}` }
        });

        if (response?.data?.user) {
          this.applyUserUpdate(response.data.user);
        }
      } catch (error) {
        console.error('Error removing role:', error);
      } finally {
        this.hideLoading();
      }
    },
    applyUserUpdate(updatedUser) {
      const resolvedRoles = Array.isArray(updatedUser?.roles) && updatedUser.roles.length
        ? updatedUser.roles
        : (this.editedUser?.roles || this.user?.roles || []);

      const normalizedUser = {
        ...updatedUser,
        roles: this.normalizeRoles(resolvedRoles),
      };

      this.editedUser = {
        ...this.editedUser,
        ...normalizedUser,
      };
      this.setUser(normalizedUser);
    },
    handleConfirmed() {
      if (!this.pendingConfirmResolver) {
        this.resetConfirmationModalState();
        return;
      }

      const resolve = this.pendingConfirmResolver;
      this.resetConfirmationModalState();
      resolve(true);
    },
    cancelModal() {
      if (!this.pendingConfirmResolver) {
        this.resetConfirmationModalState();
        return;
      }

      const resolve = this.pendingConfirmResolver;
      this.resetConfirmationModalState();
      resolve(false);
    },
    resetConfirmationModalState() {
      this.pendingConfirmResolver = null;
      this.showCancel = false;
      this.modalMessage = '';
    },
    confirmWithModal(message) {
      return new Promise((resolve) => {
        this.pendingConfirmResolver = resolve;
        this.showCancel = true;
        this.modalMessage = message;
        this.$refs.confirmationModal.openModal();
      });
    },
    openInfoModal(message) {
      this.pendingConfirmResolver = null;
      this.showCancel = false;
      this.modalMessage = message;
      this.$refs.confirmationModal.openModal();
    }
  }
}
</script>

<style scoped>
.profile-img {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 50%;
  border: 4px solid white;
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  background-color: white;
}

.card-container {
  margin-top: 35px;
}
.crapper-container{
  max-width: 550px;

}

.section-box {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
}

.section-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.identity-role-card {
  border: 1px solid #dee2e6;
  border-radius: 0.75rem;
  padding: 0.9rem 1rem;
  margin-bottom: 0.9rem;
  background: #ffffff;
}

.identity-role-title {
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.roles-list span {
  display: inline-block;
}
.roles-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.role-pill {
  padding-right: 1.5rem;
  margin-right: 0.75rem;
  margin-bottom: 0.75rem;
}
.remove-role-btn {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #6c757d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  border: 1px solid rgba(255, 255, 255, 0.35);
  text-decoration: none;
}
.roles-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.roles-modal {
  max-width: 420px;
  width: 90%;
}
.phone-row {
  gap: 0.75rem;
}
.phone-code-input {
  min-width: 90px;
  text-align: center;
}
.phone-number-input {
  min-width: 0;
}
.phone-preview {
  margin-top: 0.35rem;
}
.image-hover {
  transition: opacity 0.3s ease;
}

.card-head:hover .image-hover {
  opacity: 0.4; /* Ajusta la opacidad según sea necesario */
}

.card-head:hover .upload-icon {
  display: block; /* Muestra el ícono de '+' al hacer hover */
}

.upload-icon {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
}

.card-head {
  position: relative;
}

.fa-plus-circle {
  border: 1px solid white;
}
.card-head img {
  border-radius: 50%;
}

.card-head button {
  margin-top: 10px;
}
.alert-box {
  background-color: #fff5f5;
  border: 1px solid #f5c2c7;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  max-width: 250px;
}

.button-group .btn {
  min-width: 120px;
}


</style>
